import type { ReactElement, ReactNode } from 'react';

export interface EmptyStateAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface EmptyStateProps {
  readonly icon?: string;
  readonly heading: string;
  readonly subtext?: string;
  readonly action?: EmptyStateAction;
  /**
   * fix/dashboard-empty-state — an optional actions slot for callers that need
   * real links (<a href>) or more than one control, instead of the single
   * onClick `action` button. Rendered after the subtext.
   */
  readonly children?: ReactNode;
  /** Optional test id for the container (callers with several empty states). */
  readonly testId?: string;
}

export function EmptyState({
  icon,
  heading,
  subtext,
  action,
  children,
  testId,
}: EmptyStateProps): ReactElement {
  return (
    <div
      role="status"
      data-testid={testId}
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        color: 'var(--text-muted)',
        background: 'var(--surface2)',
        border: '1px dashed var(--border)',
        borderRadius: 8,
      }}
    >
      {icon && (
        <div aria-hidden="true" style={{ fontSize: 36, marginBottom: 8 }}>
          {icon}
        </div>
      )}
      <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)', fontSize: 18 }}>{heading}</h3>
      {subtext && (
        <p style={{ margin: '0 0 16px 0', fontSize: 14, color: 'var(--text-muted)' }}>{subtext}</p>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {action.label}
        </button>
      )}
      {children}
    </div>
  );
}
